import React, { useEffect, useRef, useState } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { LinkWithUTM as Link} from './LinkWithUTM'
import HamburgerButton from "./icons/HamburgerButton";
import CallIcon from '../components/icons/CallIcon'
import HeaderProfile from "./icons/HeaderProfile";
import logoPrimary from "../images/logos/logo_primary.svg";

/* global _paq */

export default function Header() {
    const { site: { siteMetadata: {
        appConfig: { domain: appDomain },
        featureToggles: {
            enableBlogContent
        }
    } } } = useStaticQuery(graphql`
        query HeaderQuery {
            site {
                siteMetadata {
                    appConfig {
                        domain
                    }
                    featureToggles {
                        enableBlogContent
                    }
                }
            }
        }
`)
    const navbarSelectionStates = {
        HOW_EDYN_WORKS: "HOW_EDYN_WORKS",
        CONDITIONS: "CONDITIONS",
        LOCATIONS: "LOCATIONS",
        PLANNING_AND_ADVICE: "PLANNING_AND_ADVICE",
        CLOSED: 'CLOSED',
        OPEN: 'OPEN'
    }

    const [selectedField, setSelectedField] = useState(navbarSelectionStates.CLOSED);
    const closeTimer = useRef(null);

    const setNavbarState = (newState) => {
        if (newState === selectedField) return;
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setSelectedField(newState)

        if (typeof _paq !== 'undefined') {
            _paq && _paq.push(['trackEvent', 'Navbar', 'expand', newState]);
        }
    }

    const closeNavbar = () => {
        closeTimer.current = setTimeout(() => {
            setNavbarState(navbarSelectionStates.CLOSED);
        }, 120);
    }

    const keepNavbarOpen = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }

    useEffect(() => {
        const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)

        if (vw >= 1152) {
            // same as macbook breakpoint
            return;
        }
        if (selectedField === navbarSelectionStates.CLOSED) {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        } else {
            document.body.style.position = 'fixed';
            document.body.style.top = `-${window.scrollY}px`;
        }
    }, [selectedField])

    useEffect(() => () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
        }
    }, [])

    const isExpanded = selectedField !== navbarSelectionStates.CLOSED

    return (
        <>
            <nav className={`header ${isExpanded ? "expanded" : ""}`} onMouseEnter={keepNavbarOpen} onMouseLeave={closeNavbar}>
                <div className="header__visible">
                    <div className="header__button--mobile">
                        <HamburgerButton expanded={isExpanded} onClick={() => setNavbarState(isExpanded ? navbarSelectionStates.CLOSED : navbarSelectionStates.OPEN)}/>
                    </div>
                    <Link className="header__logo-link" to="/">
                        <img
                            className="header__logo"
                            src={logoPrimary}
                            alt="edyn.care"
                        />
                    </Link>
                    <a href={`${appDomain}/login-redirect`} className="header__button--mobile">
                        <HeaderProfile />
                    </a>
                    <div className="header__links">
                        <div className="header__routes">
                            <Link className="header__route-link" to="/liveincare/">Live-in care</Link>
                            <div className={`header__dropdown-link ${selectedField === navbarSelectionStates.HOW_EDYN_WORKS ? "active" : ""}`}
                                onMouseOver={() => setNavbarState(navbarSelectionStates.HOW_EDYN_WORKS)}
                                onFocus={() => setNavbarState(navbarSelectionStates.HOW_EDYN_WORKS)}
                            >
                                How Edyn works<ChevronDown className="header__route-chevron"/>
                            </div>
                            <div className={`header__dropdown-link ${selectedField === navbarSelectionStates.CONDITIONS ? "active" : ""}`}
                                onMouseOver={() => setNavbarState(navbarSelectionStates.CONDITIONS)}
                                onFocus={() => setNavbarState(navbarSelectionStates.CONDITIONS)}
                            >
                                Conditions we care for<ChevronDown className="header__route-chevron"/>
                            </div>
                            <div className={`header__dropdown-link ${selectedField === navbarSelectionStates.LOCATIONS ? "active" : ""}`}
                                onMouseOver = {() => setNavbarState(navbarSelectionStates.LOCATIONS)}
                                onFocus={() => setNavbarState(navbarSelectionStates.LOCATIONS)}
                            >
                                Locations<ChevronDown className="header__route-chevron"/>
                            </div>
                            { enableBlogContent && 
                                <div className={`header__dropdown-link ${selectedField === navbarSelectionStates.PLANNING_AND_ADVICE ? "active" : ""}`}
                                    onMouseOver = {() => setNavbarState(navbarSelectionStates.PLANNING_AND_ADVICE)}
                                    onFocus={() => setNavbarState(navbarSelectionStates.PLANNING_AND_ADVICE)}
                                >
                                    Planning and advice<ChevronDown className="header__route-chevron"/>
                                </div>
                            }
                        </div>
                        <div className="header__buttons">
                            <div className="header__phone">
                                <CallIcon /><a href="tel:020 3970 9900">020 3970 9900</a> 
                            </div>
                            <Link to="/become-a-carer/">
                                <button className="btn-blue">Become a carer</button>
                            </Link>
                            <a href={`${appDomain}/login-redirect`}>
                                <button className="btn-primary">Login</button>
                            </a>
                        </div>
                
                    </div>
                </div>
                <div className={`header__menu ${isExpanded ? "expanded" : ""}`}>
                    <Link className="header__menu-link--mobile" to="/">Home</Link>
                    <Link className="header__menu-link--mobile" to="/liveincare/">Live-in care</Link>
                    <div className="header__menu-link--mobile">
                        <ChevronRight className="header__menu-chevron-right"/>
                        How Edyn works
                    </div>
                    <div className={`header__menu-element header__menu-how-edyn-works ${selectedField === navbarSelectionStates.HOW_EDYN_WORKS ? "visible" : "" }`}>
                        <MenuIntroCard
                            label="How Edyn works"
                            title="Care built around real life"
                            description="Explore our care model, transparent pricing and the people who make every placement feel considered."
                            link="/approach/"
                            linkText="Start with our approach"
                        />
                        <div className="header__submenu">
                            <div className="header__submenu-element">
                                <Link to="/approach/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Our approach
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/pricing/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Our pricing
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/our-carers/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Our carers
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/our-team/" target="__blank">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Our team
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <a href="https://www.trustpilot.com/review/edyn.care" target="__blank">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Customer reviews
                                </a>
	                            </div>
	                        </div>
	                        <AdvisorMenuCard />
	                    </div>
                    <div className="header__menu-link--mobile">
                        <ChevronRight className="header__menu-chevron-right"/>
                        Conditions we care for
                    </div>
                    <div className={`header__menu-element header__menu-conditions ${selectedField === navbarSelectionStates.CONDITIONS ? "visible" : "" }`}>
                        <BrochureMenuCard />
                        <div className="header__submenu">
                            <div className="header__submenu-element">
                                <Link to="/condition/dementia/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Dementia
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/condition/parkinsons/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Parkinson's
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/condition/cancer/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Cancer
                                </Link>
                            </div>
                        </div>
                        <div className="header__submenu">
                            <div className="header__submenu-element">
                                <Link to="/condition/stroke/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Stroke
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/condition/multiple-sclerosis/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    Multiple Sclerosis
                                </Link>
	                            </div>
	                        </div>
	                        <AdvisorMenuCard />
	                    </div>
                    <div className="header__menu-link--mobile">
                        <ChevronRight className="header__menu-chevron-right"/>
                        Locations
                    </div>
                    <div className={`header__menu-element header__menu-locations ${selectedField === navbarSelectionStates.LOCATIONS ? "visible" : "" }`}>
                        <MenuIntroCard
                            label="Local teams"
                            title="Care across England"
                            description="Meet the regional support available for families arranging care close to home."
                            link="/location/london/"
                            linkText="Explore locations"
                        />
                        <div className="header__submenu">
                            <div className="header__submenu-element">
                                <Link to="/location/london/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    London
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/location/east-midlands/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    East Midlands
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to= "/location/west-of-england/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    West Of England
                                </Link>
                            </div>
                        </div>
                        <div className="header__submenu">
                            <div className="header__submenu-element">
                                <Link to="/location/south-east/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    South East
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/location/south-west/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    South West
                                </Link>
                            </div>
                            <div className="header__submenu-element">
                                <Link to="/location/west-midlands/">
                                    <ChevronRight className="header__menu-chevron-right"/>
                                    West Midlands
                                </Link>
	                            </div>
	                        </div>
	                        <AdvisorMenuCard />
	                    </div>
                    { enableBlogContent &&
                        <> 
                            <div className="header__menu-link--mobile">
                            <ChevronRight className="header__menu-chevron-right"/>
                                Planning & advice
                            </div>
                            <div className={`header__menu-element header__menu-planning-and-advice ${selectedField === navbarSelectionStates.PLANNING_AND_ADVICE ? "visible" : "" }`}>
                                <MenuIntroCard
                                    label="Planning and advice"
                                    title="Clear next steps for care"
                                    description="Helpful reading for early conversations, funding questions and preparing for live-in care."
                                    link="/blog/"
                                    linkText="Visit the blog"
                                />
                                <div className="header__submenu header__submenu--articles">
                                    <div className="header__submenu-heading">
                                        <ChevronRight className="header__menu-chevron-right"/>
                                        <div className="header__submenu-content">
                                            <div className="header__submenu-title">Starting the conversation</div>
                                            <div className="header__secondary-submenu">
                                                <Link to="/blog/when-is-care-needed/">When is care needed?</Link>
                                                <Link to="/blog/what-can-a-live-in-carer-help-with/">What can a live-in carer help with?</Link>
                                                <Link to="/blog/live-in-care-vs-care-home/">Live-in care vs a care home</Link>
                                                <Link to="/blog/discussing-care-options-as-a-family/">Discussing care options as a family</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="header__submenu header__submenu--articles">
                                    <div className="header__submenu-heading">
                                        <ChevronRight className="header__menu-chevron-right"/>
                                        <div className="header__submenu-content">
                                            <div className="header__submenu-title">Preparing for care</div>
                                            <div className="header__secondary-submenu">
                                                <Link to="/blog/how-to-decide-on-the-right-carer-for-you/">How to decide on the right carer for you</Link>
                                                <Link to="/blog/what-is-a-care-assessment/">What is a care assessment?</Link>
                                                <Link to="/blog/cqc-vs-introductory-agency/">CQC vs introductory agency</Link>
                                                <Link to="/blog/what-is-a-care-consultation/">What is a care consultation?</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="header__submenu header__submenu--articles">
                                    <div className="header__submenu-heading">
                                        <ChevronRight className="header__menu-chevron-right"/>
                                        <div className="header__submenu-content">
                                            <div className="header__submenu-title">Paying for care</div>
                                            <div className="header__secondary-submenu">
                                                <Link to="/blog/state-and-local-authority-funding/">State and local authority funding</Link>
                                                <Link to="/blog/self-funding-care/">Self-funding care</Link>
                                                <Link to="/blog/what-is-the-cost-of-live-in-care/">What is the cost of live-in care?</Link>
                                                <Link to="/blog/what-are-my-funding-options/">What are my funding options?</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <AdvisorMenuCard />
                            </div>
                        </>
                    }
                    <div className="header__menu-buttons--mobile">
                        <div>
                            <a href={`${appDomain}/login-redirect`}>
                                <button className="btn-primary">Login</button>
                            </a>
                            <Link to="/cta/">
                                <button className="btn-green">Arrange a call</button>
                            </Link>
                        </div>
                        <Link to="/become-a-carer/">
                            <button className="btn-transparent">Become a carer</button>
                        </Link>
                    </div>
                </div>
            </nav>
            <div className={`header__vertical-spacer ${selectedField === navbarSelectionStates.CLOSED ? 'header__vertical-spacer--hide' : 'header__vertical-spacer--show'}`} />
        </>
    );
}

const MenuIntroCard = ({ label, title, description, link, linkText }) => (
    <div className="header__menu-card">
        <div className="header__menu-card-icon">
            <SparkleIcon />
        </div>
        <p className="header__menu-card-label">{label}</p>
        <h3>{title}</h3>
        <p>{description}</p>
        <Link to={link}>{linkText}<ChevronRight /></Link>
    </div>
)

const BrochureMenuCard = () => (
    <div className="header__menu-card header__menu-card--brochure">
        <div className="header__menu-card-copy">
            <p className="header__menu-card-label">Free guide</p>
            <h3>Live-in care guide</h3>
            <p>Understand your care options, costs and next steps with our helpful brochure.</p>
            <Link to="/request-a-brochure/">Request a brochure<ChevronRight /></Link>
        </div>
        <img
            src="/assets/marketing-site/images/banner-brochure.png"
            alt="Live-in care brochure"
        />
    </div>
)

const showLiveChat = () => {
    if (typeof window !== "undefined" && window.Intercom) {
        window.Intercom("show");
    }
}

const AdvisorMenuCard = () => (
    <div className="header__advisor-card">
        <div className="header__advisor-copy">
            <p className="header__menu-card-label">Care advice</p>
            <h3>Talk to a Family Care Advisor</h3>
            <p className="header__advisor-description">
                Compassionate support from our expert team.
            </p>
        </div>
        <div className="header__advisor-images" aria-hidden="true">
            <StaticImage className="header__advisor-image header__advisor-image--main" src="../../static/assets/marketing-site/images/team/orianne.png" alt="" />
            <StaticImage className="header__advisor-image header__advisor-image--top" src="../../static/assets/marketing-site/images/team/matthew.png" alt="" />
            <StaticImage className="header__advisor-image header__advisor-image--bottom" src="../../static/assets/marketing-site/images/team/daniel.png" alt="" />
        </div>
        <div className="header__advisor-actions">
            <button type="button" onClick={showLiveChat} className="header__advisor-action">
                <ChatIcon />
                Live chat
            </button>
            <a href="tel:02039709900" className="header__advisor-action">
                <CallIcon />
                020 3970 9900
            </a>
        </div>
        <Link to="/cta/" className="header__advisor-cta">Arrange a call back</Link>
    </div>
)

const SparkleIcon = props =>
    <svg {...props} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 1.5L10.8 6.6L16.5 9L10.8 11.4L9 16.5L7.2 11.4L1.5 9L7.2 6.6L9 1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>

const ChatIcon = props =>
    <svg {...props} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor"/>
        <path d="M4.1 9.7C3.73 9.23 3.55 8.68 3.55 8.08C3.55 6.55 4.95 5.33 6.68 5.33C8.4 5.33 9.8 6.55 9.8 8.08C9.8 9.6 8.4 10.83 6.68 10.83C6.15 10.83 5.65 10.7 5.22 10.5L3.82 10.83L4.1 9.7Z" stroke="white" strokeWidth="0.75" strokeLinejoin="round"/>
        <path d="M5.43 8.08H5.44M6.68 8.08H6.69M7.93 8.08H7.94" stroke="white" strokeWidth="1.1" strokeLinecap="round"/>
    </svg>



const ChevronDown = props => 
    <svg {...props} width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.82873 1L4.0003 3.82843L1.17188 1" stroke="currentColor"/>
    </svg>

const ChevronRight = props =>
    <svg {...props}  width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.82812 0.999884L5.65655 3.82831L2.82812 6.65674" stroke="currentColor"/>
    </svg>
